// L07 · Be Still (SPEC §3/§6, Act III). Third inverted biometric — the cruelest one.
// "Place your finger on the sensor. Do not move. Ten seconds." A human hand cannot hold
// perfectly still; involuntary micro-tremor is the proof of life. Total drift under 3px
// over the window is the failure: "Nothing. Not one tremor. Stone is still. Are you stone?"
// The live waveform is drawn as it happens — both the evidence and a screenshot magnet.
//
// Pointer samples never leave the device. Traits: `steady` / `trembling` (both compliments).

import { el } from "@/ui/chrome.ts";
import { runLevel, promptEl } from "@/levels/util.ts";
import {
  TremorSampler,
  tremorStats,
  ZERO_DRIFT_PATH,
  TREMBLE_PATH,
  type Point,
} from "@/engine/biometrics.ts";
import { L07 as V } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

const W = 260;
const H = 140;
const HOLD_MS = 10_000;
// A lift within this window reads as an accidental tap → let them place again, not a verdict.
const ACCIDENTAL_MS = 1200;

function isDesktop(): boolean {
  return (
    typeof matchMedia === "function" &&
    matchMedia("(hover: hover) and (pointer: fine)").matches
  );
}

export const L07: Level = {
  id: "L07",
  act: 3,
  title: "Be Still",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      const desktop = isDesktop();
      host.append(promptEl(desktop ? V.promptDesktop : V.promptTouch));

      const canvas = el("canvas", "pad pad--sensor");
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      canvas.setAttribute("role", "img");
      canvas.setAttribute("aria-label", "Stillness sensor");
      host.append(canvas);
      canvas.getContext("2d")!.scale(dpr, dpr);

      const hint = el("div", "pad__hint");
      hint.textContent = desktop ? "press and hold — do not move" : "hold — do not move";
      host.append(hint);

      // Dev-only shortened window so the headless driver / manual iteration is not gated on
      // a real ten-second hold. Production always uses the full ten seconds.
      let holdMs = HOLD_MS;
      if (import.meta.env.DEV) {
        const raw = new URLSearchParams(location.search).get("hold");
        const n = raw !== null ? Number.parseInt(raw, 10) : NaN;
        if (Number.isFinite(n) && n > 0) holdMs = n;
      }

      const sampler = new TremorSampler(canvas);
      let engaged = false;
      let startedAt = 0;
      let holdTimer = 0;
      let tickTimer = 0;

      const at = (e: PointerEvent): Point => {
        const rect = canvas.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
      };

      const stopTimers = () => {
        window.clearTimeout(holdTimer);
        window.clearInterval(tickTimer);
      };

      const finishHold = () => {
        stopTimers();
        const path = sampler.stop();
        const { pathLength } = tremorStats(path);
        if (pathLength < ZERO_DRIFT_PATH) {
          done({ pass: true, strike: true, line: V.stone });
        } else if (pathLength > TREMBLE_PATH) {
          done({ pass: true, line: V.trembling, traits: ["trembling"] });
        } else {
          done({ pass: true, line: V.steady, traits: ["steady"] });
        }
      };

      const onDown = (e: PointerEvent) => {
        if (engaged) return;
        e.preventDefault();
        engaged = true;
        startedAt = performance.now();
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          /* capture is best-effort */
        }
        sampler.begin(at(e));
        holdTimer = window.setTimeout(finishHold, holdMs);
        const render = () => {
          const remaining = Math.max(0, holdMs - (performance.now() - startedAt));
          hint.textContent = `${V.holding} ${Math.ceil(remaining / 1000)}`;
        };
        render();
        tickTimer = window.setInterval(render, 200);
      };

      const onLift = () => {
        if (!engaged) return;
        const elapsed = performance.now() - startedAt;
        if (elapsed < ACCIDENTAL_MS) {
          // Treat as an accidental tap — reset and let them settle in.
          stopTimers();
          sampler.stop();
          engaged = false;
          hint.textContent = desktop ? "press and hold — do not move" : "hold — do not move";
          return;
        }
        // A genuine hold, then a lift — the bold/trackpad branch (§3). Passes, unflagged.
        stopTimers();
        sampler.stop();
        done({ pass: true, line: V.lifted });
      };

      canvas.addEventListener("pointerdown", onDown);
      canvas.addEventListener("pointerup", onLift);
      canvas.addEventListener("pointercancel", onLift);

      return () => {
        stopTimers();
        sampler.stop();
      };
    });
  },
};
